/*
 * -----------------------------------------------------------------------
 *  <copyright file="index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { DocumentNode } from '@apollo/client';
import { ApolloError } from '@apollo/client/v4-migration';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTableKeyboardNavigation } from '@progress/kendo-react-data-tools';
import { GRID_COL_INDEX_ATTRIBUTE } from '@progress/kendo-react-grid';
import cn from 'clsx';
import { produce } from 'immer';
import { set, has } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState, RefObject } from 'react';
import {
    useBoolean as hangerUseBoolean,
    useInput as hangerUseInput,
    useMap as hangerUseMap,
    useStateful as hangerUseStateful,
} from 'react-hanger';
import { useTranslation as i18nUseTranslation } from 'react-i18next';
import Moment from 'react-moment';
import InactiveIcon from '../views/components/core/inactive-icon';
import { IModule } from 'config/modules';
import { LayoutContext } from 'contexts/layout-context';
import { LocalStateContext } from 'contexts/local-state-context';
import { Permissions, UserContext } from 'contexts/user-context';
import AuthRules from 'utils/auth-rules';
import Constants from 'utils/constants';
import { showMaxLength } from 'utils/filter';
import { getFormat } from 'utils/generator';
import { convertBooleanToYesNo } from '@utils/convertor';
import { MOVEMENT_STATE } from '@utils/risk.service';
import { isSplittedValue, isStringNumeric } from '@utils/validation';
import { IRoute } from '@config/routes/interfaces';

export const useTranslation = i18nUseTranslation;
export const useBoolean = hangerUseBoolean;
export const useInput = hangerUseInput;
export const useMap = hangerUseMap;
export const useStateful = hangerUseStateful;

const IGNORE_TIME = 0x01;
const IGNORE_DAY = 0x02;

const { environment } = window;

export const useLogger = () => ({
    log: (...props: any[]) => {
        if (environment?.toLowerCase()?.indexOf('prod') < 0) console.log(...props);
    },
    warn: (...props: any[]) => {
        if (environment?.toLowerCase()?.indexOf('prod') < 0) console.warn(...props);
    },
    error: (...props: any[]) => {
        console.error(...props);
    },
});

export const usePartialDate = () => ({
    display: (date: any, partialFormat: string = 'MMM YYYY', fullFormat: string = 'D MMM YYYY') => {
        if (!date || !date.time) return '';

        let format = fullFormat;

        if (date.ignore & IGNORE_DAY) format = partialFormat;
        else if (date.ignore & IGNORE_TIME) format = fullFormat;
        else format = 'D MMM YYYY H:mm:ss a';

        return <Moment format={format}>{date.time}</Moment>;
    },
});

const getDate = (date: any) => (!date ? null : typeof date === 'string' ? new Date(date) : date);

// deprecated - please use convertDateTimeToMoment (utils-clean/date-time-helpers.tsx) for "display" from now on -
// or the methods in convertor.tsx (utils/convertor.tsx) for display date / display time
export const useDateTime = () => ({
    display: (date: any, format?: string) => {
        const d = getDate(date);
        return !d ? (
            ''
        ) : d.getUTCFullYear() === 1 ? (
            'Invalid Date'
        ) : (
            <Moment format={format && format !== '{0:undefined}' ? format : 'DD-MMM-YYYY H:mm:ss a'} utc>
                {d}
            </Moment>
        );
    },

    displayDate: (date: any) => {
        const actualDate = date ? moment(date) : null;

        return !actualDate ? '' : actualDate.year() === 1 ? 'Invalid Date' : actualDate.utc().format('DD-MMM-YYYY');
    },
});

const dateTime = useDateTime();
export const useGridCells = () => ({
    CustomizableCell: (renderComponent: any) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <p>{renderComponent(dataItem)}</p>
                </td>
            );
        },

    SplittedValuesCell: (pathValue: string, pathStatus: string) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const value = pathValue?.split('.').reduce((o, i) => o?.[i], dataItem) ?? '';
            const status = pathStatus?.split('.').reduce((o, i) => o?.[i], dataItem) ?? '';
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const isColorStatus = Constants.EvaluationColour.includes(status);
            const isDirectionStatus = Constants.DirectionStatuses.includes(status);
            const colorClass = status?.toLowerCase() ?? '';
            const mainValue = isStringNumeric(value) ? Number(value)?.toFixed(2) : value;

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <div className="aligned-two-items">
                        {isColorStatus && (
                            <>
                                <div className={cn('circle-rating', colorClass)} />
                                &nbsp;{mainValue}
                            </>
                        )}
                        {isDirectionStatus && (
                            <>
                                {mainValue}
                                &nbsp;
                                <strong>{status}</strong>
                            </>
                        )}
                        {!isColorStatus && !isDirectionStatus && mainValue}
                    </div>
                </td>
            );
        },

    // eslint-disable-next-line no-undef
    ActiveInactiveCell: (
        inactive: (dataItem: any) => boolean,
        renderValue: (dataItem: any) => string | JSX.Element,
        multiple?: boolean,
        customField?: string,
    ) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const itemField = customField || field;

            const getContentItem = (item: any) => (
                <span style={{ display: 'inline-block' }}>
                    {inactive(item) && <InactiveIcon />}
                    {renderValue(item)}
                </span>
            );

            const content = multiple
                ? dataItem[itemField]?.map((value: any, index: number, array: []) => (
                      <span style={{ display: 'inline-block' }}>
                          {getContentItem(value)}
                          {index < array.length - 1 && <span style={{ marginRight: '5px' }}>,</span>}
                      </span>
                  ))
                : getContentItem(dataItem);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <p>{content}</p>
                </td>
            );
        },

    CellWithChangeIcon: (fieldName: string, decimals?: number) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const icon =
                dataItem[fieldName]?.change?.toLowerCase() === 'upper'
                    ? 'arrow-circle-up'
                    : dataItem[fieldName]?.change?.toLowerCase() === 'lower'
                      ? 'arrow-circle-down'
                      : 'minus-circle';

            if (!dataItem.siteToShow)
                return (
                    <td
                        className={cn(className || '', rest.tdProps?.className)}
                        style={className.match('sticky') ? { ...style } : null}
                        {...rest.tdProps}
                        colSpan={rest.colSpan}
                        role="gridcell"
                        aria-colindex={rest.ariaColumnIndex}
                        aria-selected={rest.isSelected}
                        {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                        {...navigationAttributes}
                    >
                        --
                    </td>
                );

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    {dataItem[fieldName]?.colour ? (
                        <FontAwesomeIcon
                            icon={['far', icon]}
                            className={`arrow-circle ${dataItem[fieldName]?.colour?.toLowerCase() || 'black'}`}
                        />
                    ) : null}
                    &nbsp;
                    {dataItem[fieldName]?.score > 0
                        ? decimals
                            ? (+dataItem[fieldName]?.score).toFixed(decimals)
                            : dataItem[fieldName]?.score
                        : '--'}
                    {dataItem[fieldName]?.label === 'U' || dataItem[fieldName]?.label === 'L'
                        ? dataItem[fieldName]?.label
                        : ''}
                </td>
            );
        },
    MovementCell: (fieldName: string, withValue?: boolean) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            const changeObject = fieldName.split('.').reduce((o, i) => o?.[i], dataItem);
            const changeDirection = withValue ? changeObject?.change : dataItem[fieldName];
            const icon =
                changeDirection === MOVEMENT_STATE.rising
                    ? 'arrow-circle-up'
                    : changeDirection === MOVEMENT_STATE.falling
                      ? 'arrow-circle-down'
                      : 'minus-circle';

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    {!withValue && <FontAwesomeIcon icon={['far', icon]} className="arrow-circle black" />}
                    {withValue &&
                        changeObject?.score !== null &&
                        changeObject?.score !== '' &&
                        changeObject?.score !== undefined && (
                            <FontAwesomeIcon icon={['far', icon]} className="arrow-circle black" />
                        )}
                    &nbsp;
                    {withValue ? changeObject?.score : ''}
                </td>
            );
        },

    StandardCell: (isCapitalize?: boolean, showDashes?: boolean) =>
        function ({ className, dataItem, field, style, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <p className={isCapitalize ? 'type-text' : ''}>{dataItem[field] ?? (showDashes ? '--' : '')}</p>
                </td>
            );
        },

    PartialDateCell: (fieldName?: string) =>
        function ({ className, dataItem, style, field, ...rest }: any) {
            const format = getFormat(dataItem[fieldName]?.ignore);
            const navigationAttributes = useTableKeyboardNavigation(rest.id);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    {dataItem && dateTime.display(dataItem[fieldName]?.time, format)}
                </td>
            );
        },

    DateCell({ className, dataItem, style, field, dateFormat, renderCell, ...rest }: any) {
        const navigationAttributes = useTableKeyboardNavigation(rest.id);
        const currentDateValue = renderCell ? renderCell(field, dataItem) : dataItem[field];
        const dateResult = isSplittedValue(currentDateValue) ? currentDateValue.split('|')[0] : currentDateValue;
        const dateFormatResult = isSplittedValue(currentDateValue)
            ? currentDateValue.split('|')[1]
            : (dateFormat ?? null);

        return (
            <td
                className={cn(className || '', rest.tdProps?.className)}
                style={className.match('sticky') ? { ...style } : null}
                {...rest.tdProps}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {!!dateFormatResult
                    ? dataItem && dateTime.display(dateResult, dateFormatResult)
                    : dataItem && dateTime.displayDate(dateResult)}
            </td>
        );
    },

    DateTimeCell: (format?: string) =>
        function ({ className, dataItem, style, field, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    {dateTime.display(dataItem[field], format)}
                </td>
            );
        },

    YesNoCell: ({ className, dataItem, style, field, renderCell, ...rest }: any) => {
        const navigationAttributes = useTableKeyboardNavigation(rest.id);
        const currentBoolValue = renderCell ? renderCell(field, dataItem) : dataItem[field];

        return (
            <td
                className={cn(className || '', rest.tdProps?.className)}
                style={className.match('sticky') ? { ...style } : null}
                {...rest.tdProps}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {convertBooleanToYesNo(currentBoolValue)}
            </td>
        );
    },

    MaxLengthCell: (max: number, maxHeight?: string) =>
        function ({ className, dataItem, style, field, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);
            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    <p>{showMaxLength(dataItem[field]?.trim(), max, maxHeight)}</p>
                </td>
            );
        },

    NumberCell: ({ className, style, dataItem, field, ...rest }: any) => {
        const navigationAttributes = useTableKeyboardNavigation(rest.id);

        return (
            <td
                className={cn(className || '', rest.tdProps?.className)}
                style={className.match('sticky') ? { ...style } : null}
                {...rest.tdProps}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {dataItem[field] ?? ''}
            </td>
        );
    },

    RoundedNumberCell: (numPlaces: number = 0) =>
        function ({ className, dataItem, style, field, ...rest }: any) {
            const navigationAttributes = useTableKeyboardNavigation(rest.id);

            return (
                <td
                    className={cn(className || '', rest.tdProps?.className)}
                    style={className.match('sticky') ? { ...style } : null}
                    {...rest.tdProps}
                    colSpan={rest.colSpan}
                    role="gridcell"
                    aria-colindex={rest.ariaColumnIndex}
                    aria-selected={rest.isSelected}
                    {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                    {...navigationAttributes}
                >
                    {dataItem[field] !== null ? parseFloat(dataItem[field] ?? 0).toFixed(numPlaces) : ''}
                </td>
            );
        },
});

export const useLayout = () => useContext(LayoutContext);

export const useLocalState = () => useContext(LocalStateContext);

export const useAuthorization = () => {
    const userContext = useContext(UserContext);
    const { state } = useLocalState();

    const user = userContext?.user;

    const getMember = () => user.organisation?.members.find((orgMember: any) => orgMember.userId === user.id);

    if (user) {
        user.token = userContext.token;

        user.setUser = userContext.setUser;

        user.can = (what: any) => AuthRules.isValid(user, state?.selectedStudy, what);

        user.canRead = user.isDisabled = ({ module, element }: any) =>
            user.systemAdminLevel > Constants.SystemAdminLevel.None
                ? true
                : user.can({ module, element, permission: Permissions.Read });

        user.canWrite = user.isEnabled = ({ module, element }: any) =>
            user.systemAdminLevel > 0
                ? user.systemAdminLevel === Constants.SystemAdminLevel.SysAdmin
                    ? false
                    : user.systemAdminLevel >= Constants.SystemAdminLevel.Contributor
                : user.can({ module, element, permission: Permissions.ReadWrite });

        user.canWriteSystemAdminLevel = user.isEnabled = (
            checkOrg?: boolean,
            checkStudy?: boolean,
            checkReport?: boolean,
        ) =>
            (user.systemAdminLevel >= Constants.SystemAdminLevel.Contributor &&
                user.systemAdminLevel !== Constants.SystemAdminLevel.SysAdmin) ||
            (checkOrg ? user.isOrgAdmin(checkOrg) : false) ||
            (checkStudy ? user.isStudyAdmin(checkStudy) : false) ||
            (checkReport ? user.isReportAdmin(true) : false);

        user.canWriteSystemSmAdminLevel = user.isEnabled = (
            checkOrg?: boolean,
            checkStudy?: boolean,
            checkReport?: boolean,
        ) =>
            (user.systemAdminLevel >= Constants.SystemAdminLevel.Contributor &&
                user.systemAdminLevel !== Constants.SystemAdminLevel.SysAdmin) ||
            (checkOrg ? user.isOrgAdmin(checkOrg) : false) ||
            (checkStudy ? user.isSmStudyAdmin(checkStudy) : false) ||
            (checkReport ? user.isReportAdmin(true) : false);

        user.canReadStudyAdminLevel = user.isEnabled = () =>
            (user.systemAdminLevel > 0 && user.systemAdminLevel !== Constants.SystemAdminLevel.Contributor) ||
            (user.systemAdminLevel === 0 && user.isStudyAdmin());

        user.canReadSmStudyAdminLevel = user.isEnabled = () =>
            (user.systemAdminLevel > 0 && user.systemAdminLevel !== Constants.SystemAdminLevel.Contributor) ||
            (user.systemAdminLevel === 0 && user.isSmStudyAdmin());

        user.isSystemAdmin = (level: number = Constants.SystemAdminLevel.Reviewer) => user.systemAdminLevel >= level;

        user.isActiveOrgMember = () => {
            const member = getMember();
            return member && member.isActive;
        };

        user.isOrgAdmin = (skipSystemLevel?: boolean) => {
            if (user.systemAdminLevel > 0 && !skipSystemLevel) return true;

            const member = getMember();
            return member && member.isActive && member.isAdmin;
        };

        user.isStudyAdmin = (skipSystemLevel?: boolean) => {
            if (user.systemAdminLevel > 0 && !skipSystemLevel) return true;

            const member = state?.selectedStudy?.members?.find((studyMember: any) => studyMember.userId === user.id);

            return member && member.active && member.isAdmin;
        };

        user.isSmStudyAdmin = (skipSystemLevel?: boolean) => {
            if (user.systemAdminLevel > 0 && !skipSystemLevel) return true;

            const member = state?.selectedStudy?.smMembers?.find((studyMember: any) => studyMember.userId === user.id);
            return member && member.active && member.isAdmin;
        };

        user.isAuditAdmin = () => {
            if (user.systemAdminLevel > 0) return true;

            const member = user.organisation?.members.find((orgMember: any) => orgMember.userId === user.id);
            return member && member.isActive && member.isAuditAdmin;
        };

        user.isReportAdmin = (skipSystemLevel?: boolean) => {
            if (user.systemAdminLevel > 0 && !skipSystemLevel) return true;

            const member = user.organisation?.members.find((orgMember: any) => orgMember.userId === user.id);
            return member && member.isActive && member.isReportAdmin;
        };

        user.isObsAndLogsAdmin = () => {
            if (
                [
                    Constants.SystemAdminLevel.Reviewer,
                    Constants.SystemAdminLevel.Contributor,
                    Constants.SystemAdminLevel.Administrator,
                    Constants.SystemAdminLevel.SysAdmin,
                ].includes(user.systemAdminLevel)
            ) {
                return true;
            }

            const member = user.organisation?.members.find((orgMember: any) => orgMember.userId === user.id);
            return member && member.isActive && member.isObsAndLogsAdmin;
        };

        user.organisationHasModule = (module: IModule) => user?.organisation?.modules?.indexOf(module.id) >= 0;

        // Returns true if the organisation has the module enabled, the current study
        // has the module enabled, and the user's role has at least one active page within
        // the module.
        user.hasModule = (module: IModule) => {
            if (user.isSystemAdmin()) return true;

            const isLoggingEnabled = import.meta.env.VITE_QUIETEN_LOGS !== 'true';

            if (user?.organisation?.modules?.indexOf(module.id) < 0) {
                isLoggingEnabled &&
                    console.log("Organisation doesn't have module: ", module, user.organisation?.modules);
                return false;
            }

            if (state.selectedStudy && (state.selectedStudy?.modules & module.value) !== module.value) {
                isLoggingEnabled && console.log("Study doesn't have module: ", module, state.selectedStudy);
                return false;
            }

            const rights = user?.role?.rights;
            if (!rights) {
                isLoggingEnabled && console.log("User doesn't have any role rights: ", user);
                return false;
            }

            const permission = !!rights.find(
                (right: any) =>
                    right.moduleId === module.value &&
                    right.element?.startsWith('page-') &&
                    right.permissions === Permissions.Enabled,
            );

            if (!permission && isLoggingEnabled) console.log("User doesn't have permission: ", rights, module);

            return permission;
        };

        user.admininstratorCanViewRoute = (route: IRoute) => {
            return !(
                ((route.admin ?? 0) > 0 && !user?.isSystemAdmin(route.admin ?? 0)) ||
                (route.exclude && route.exclude.indexOf(user.systemAdminLevel) >= 0)
            );
        };

        user.nonAdmininstratorCanViewRoute = (route: IRoute) => {
            return !(
                ((route.admin ?? 0) > 0 && !user?.isSystemAdmin(route.admin ?? 0)) ||
                (route.orgAdmin && !user?.isOrgAdmin?.() && !route.auditAdmin) ||
                (route.studyAdmin && !user?.isStudyAdmin?.()) ||
                (route.isSmStudyAdmin && !user?.isSmStudyAdmin?.()) ||
                (route.auditAdmin && !user?.isAuditAdmin?.() && !route.orgAdmin) ||
                (route.reportAdmin && !user?.isReportAdmin?.() && !route.orgAdmin) ||
                (route.auditAdmin &&
                    route.orgAdmin &&
                    route.reportAdmin &&
                    !user?.isAuditAdmin?.() &&
                    !user?.isOrgAdmin?.() &&
                    !user?.isReportAdmin?.()) ||
                (route.exclude && route.exclude.indexOf(user.systemAdminLevel) >= 0)
            );
        };
    }

    return user;
};

export interface Setting {
    key: string;
    value: any;
}

export const useSettings = () =>
    // const user = useAuthorization();
    // const [setSettings] = useMutation(SET_USER_SETTINGS);
    ({
        set: (key: string, value: any) => {
            /*
    To be implemented in a later release, for now we do not remember
    user settings either within or between sessions.

setSettings({ variables: {
    settings: [{
        key,
        value: JSON.stringify(value)
    }]
}})
    .then(result => {
        user.settings[key] = value;
    })
    .catch(e => console.log("Error saving setting: ", e));
*/
        },
        setMany: (settings: Setting[]) => {
            /*
setSettings({ variables: {
    settings: settings.map(setting => ({
        key: setting.key,
        value: JSON.stringify(setting.value)
    }))
}})
    .then(() => {
        // Update user settings
        settings.forEach(setting => user.settings[setting.key] = setting.value);
    })
    .catch(e => console.log("Error saving settings: ", e));

*/
        },
        get: (key: string) =>
            // user ?
            //    user.settings[key] :
            null as any,
    });

export const useDebounce = (value: any, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export const useBase64 = () => ({
    // From: https://stackoverflow.com/questions/36280818/how-to-convert-file-to-base64-in-javascript
    get: (file: any) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                let encoded = reader.result.toString().replace(/^data:(.*,)?/, '');
                if (encoded.length % 4 > 0) {
                    encoded += '='.repeat(4 - (encoded.length % 4));
                }
                resolve(encoded);
            };
            reader.onerror = (error) => reject(error);
        }),
});

// For handling complex forms with nested data
// https://levelup.gitconnected.com/handling-complex-form-state-using-react-hooks-76ee7bc937
export const useEnhancedReducer = (initialState: any) => {
    const enhancedReducer = (newState: any, updateArg: any) => {
        // check if the type of update argument is a callback function
        if (updateArg.constructor === Function) return { ...newState, ...updateArg(newState) };

        // if the type of update argument is an object
        if (updateArg.constructor === Object) {
            // does the update object have _path and _value as it's keys
            // if yes then use them to update deep object values
            if (has(updateArg, '_path') && has(updateArg, '_value')) {
                const { _path, _value } = updateArg;

                return produce(newState, (draft: any) => {
                    set(draft, _path, _value);
                });
            }

            return { ...newState, ...updateArg };
        }
    };

    const [state, setState] = React.useReducer(enhancedReducer, initialState);
    const updateState = React.useCallback(({ target: { value, name, type } }) => {
        const updatePath = name?.split('.');

        // if the input is a checkbox then use callback function to update
        // the toggle state based on previous state
        if (type === 'checkbox') {
            setState((prevState: any) => ({
                [name]: !prevState[name],
            }));

            return;
        }

        // if we have to update the root level nodes in the form
        if (updatePath?.length === 1) {
            const [key] = updatePath;

            setState({
                [key]: value,
            });
        }

        // if we have to update nested nodes in the form object
        // use _path and _value to update them.
        if (updatePath?.length >= 2) {
            setState({
                _path: updatePath,
                _value: value,
            });
        }
    }, []);

    return [state, updateState, setState];
};

export const useWindowSize = () => {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // Set window width/height to state
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []); // Empty array ensures that effect is only run on mount

    return windowSize;
};

const getDataString = (result: any) => {
    let resultString = '';

    Object.keys(result).forEach((key) => {
        if (typeof result[key] === 'string') resultString += result[key];
    });

    return resultString;
};

export const useLazyQueryConvertToJson = (
    query: DocumentNode,
    onCompleted?: (convertedData: any) => void,
): [Function, { convertedData: any; error: ApolloError; loading: boolean }] => {
    const [convertedData, setConvertedData] = useState();

    const [load, { data, error, loading }] = useLazyQuery(query);

    useEffect(() => {
        if (data) {
            const dataString = getDataString(data);

            if (!dataString) return;

            const result = JSON.parse(dataString);

            setConvertedData({ ...result });

            if (onCompleted) onCompleted(result);
        }
    }, [data]);

    return [load, { convertedData, error, loading }];
};

export const useQueryConvertToJson = (
    query: DocumentNode,
    variables?: any,
): { convertedData: any; error: ApolloError; loading: boolean } => {
    const [convertedData, setConvertedData] = useState();

    const { data, error, loading } = useQuery(query, { variables });

    useEffect(() => {
        if (data) {
            const dataString = getDataString(data);

            if (!dataString) return;

            const result = JSON.parse(dataString);

            setConvertedData({ ...result });
        }
    }, [data]);

    return { convertedData, error, loading };
};
export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
    ref: RefObject<T>,
    handler: (event: MouseEvent | TouchEvent) => void,
    excludedRefs?: Array<RefObject<HTMLElement>>,
) => {
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            const el = ref?.current;
            const target = event?.target as Node;

            if (
                !el ||
                el.contains(target) ||
                (excludedRefs && excludedRefs.some((excludedRef) => excludedRef.current?.contains(target)))
            ) {
                return;
            }

            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler, excludedRefs]);
};

export const useOverviewTabs = () => {
    const [hasUnsaveChanges, setHasUnsaveChanges] = useState<boolean>(false);

    return {
        setChangesContain: (hasChanges: boolean) => setHasUnsaveChanges(hasChanges),
        getChangesContain: () => hasUnsaveChanges,
    };
};
